/**
 * Web Notification Service
 * Handles browser notifications for task reminders, daily notes, etc.
 */

export interface ScheduledNotification {
  id: string;
  title: string;
  body: string;
  scheduledTime: Date;
  data?: Record<string, unknown>;
  timerId?: ReturnType<typeof setTimeout>;
}

class NotificationService {
  private scheduledNotifications: Map<string, ScheduledNotification> = new Map();
  private permissionGranted: boolean = false;
  private serviceWorkerRegistration: ServiceWorkerRegistration | null = null;

  /**
   * Initialize the notification service
   */
  async initialize(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return false;
    }

    // Check current permission
    if (Notification.permission === 'granted') {
      this.permissionGranted = true;
    } else if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      this.permissionGranted = permission === 'granted';
    }

    // Register service worker for persistent notifications (optional enhancement)
    if ('serviceWorker' in navigator && this.permissionGranted) {
      try {
        this.serviceWorkerRegistration = await navigator.serviceWorker.ready;
      } catch (error) {
        console.warn('Service worker not available for notifications');
      }
    }

    // Load any saved scheduled notifications from localStorage
    this.loadScheduledNotifications();

    return this.permissionGranted;
  }

  /**
   * Request notification permission
   */
  async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      return false;
    }

    const permission = await Notification.requestPermission();
    this.permissionGranted = permission === 'granted';
    return this.permissionGranted;
  }

  /**
   * Check if notifications are enabled
   */
  isEnabled(): boolean {
    return this.permissionGranted;
  }

  /**
   * Get current permission status
   */
  getPermissionStatus(): NotificationPermission | 'unsupported' {
    if (!('Notification' in window)) {
      return 'unsupported';
    }
    return Notification.permission;
  }

  /**
   * Send an immediate notification
   */
  async sendNotification(
    title: string,
    options?: NotificationOptions & { data?: Record<string, unknown> }
  ): Promise<Notification | null> {
    if (!this.permissionGranted) {
      console.warn('Notification permission not granted');
      return null;
    }

    try {
      const notification = new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        ...options,
      });

      notification.onclick = () => {
        window.focus();
        notification.close();

        // Handle navigation based on notification data
        if (options?.data?.objectId) {
          window.location.hash = `/object/${options.data.objectId}`;
        } else if (options?.data?.type === 'daily-note') {
          window.location.hash = '/daily';
        }
      };

      return notification;
    } catch (error) {
      console.error('Failed to send notification:', error);
      return null;
    }
  }

  /**
   * Schedule a notification for a future time
   */
  scheduleNotification(
    id: string,
    title: string,
    body: string,
    scheduledTime: Date,
    data?: Record<string, unknown>
  ): boolean {
    if (!this.permissionGranted) {
      console.warn('Notification permission not granted');
      return false;
    }

    const now = new Date();
    const delay = scheduledTime.getTime() - now.getTime();

    if (delay <= 0) {
      console.warn('Scheduled time is in the past');
      return false;
    }

    // Cancel existing notification with same ID
    this.cancelNotification(id);

    const timerId = setTimeout(() => {
      this.sendNotification(title, { body, data });
      this.scheduledNotifications.delete(id);
      this.saveScheduledNotifications();
    }, delay);

    const notification: ScheduledNotification = {
      id,
      title,
      body,
      scheduledTime,
      data,
      timerId,
    };

    this.scheduledNotifications.set(id, notification);
    this.saveScheduledNotifications();

    return true;
  }

  /**
   * Schedule a task reminder
   */
  scheduleTaskReminder(
    taskId: string,
    taskName: string,
    dueDate: Date,
    reminderMinutesBefore: number = 30
  ): boolean {
    const now = new Date();
    if (dueDate <= now) return false;

    const reminderTime = new Date(dueDate.getTime() - reminderMinutesBefore * 60 * 1000);

    // If the offset time has already passed but the due time hasn't,
    // fall back to firing at the due time (matches mobile behavior).
    const actualFireTime = reminderTime <= now ? dueDate : reminderTime;
    const body = reminderTime <= now
      ? `"${taskName}" is due now`
      : `"${taskName}" is due ${reminderMinutesBefore === 0 ? 'now' : `in ${this.formatMinutes(reminderMinutesBefore)}`}`;

    return this.scheduleNotification(
      `task-${taskId}`,
      'Task Reminder',
      body,
      actualFireTime,
      { type: 'task-reminder', objectId: taskId }
    );
  }

  /**
   * Get the fire time of a scheduled notification (for reconciliation).
   */
  getScheduledFireTime(id: string): Date | null {
    return this.scheduledNotifications.get(id)?.scheduledTime ?? null;
  }

  /**
   * Schedule a daily note reminder
   */
  scheduleDailyNoteReminder(hour: number = 8, minute: number = 0): boolean {
    const now = new Date();
    let reminderTime = new Date();
    reminderTime.setHours(hour, minute, 0, 0);

    // If the time has passed today, schedule for tomorrow
    if (reminderTime <= now) {
      reminderTime.setDate(reminderTime.getDate() + 1);
    }

    return this.scheduleNotification(
      'daily-note-reminder',
      'Daily Note Reminder',
      "Time to start your daily note and plan your day!",
      reminderTime,
      { type: 'daily-note' }
    );
  }

  /**
   * Cancel a scheduled notification
   */
  cancelNotification(id: string): boolean {
    const notification = this.scheduledNotifications.get(id);
    if (notification?.timerId) {
      clearTimeout(notification.timerId);
      this.scheduledNotifications.delete(id);
      this.saveScheduledNotifications();
      return true;
    }
    return false;
  }

  /**
   * Cancel all task reminders
   */
  cancelAllTaskReminders(): void {
    for (const [id, notification] of this.scheduledNotifications) {
      if (id.startsWith('task-') && notification.timerId) {
        clearTimeout(notification.timerId);
        this.scheduledNotifications.delete(id);
      }
    }
    this.saveScheduledNotifications();
  }

  /**
   * Cancel the daily note reminder
   */
  cancelDailyNoteReminder(): void {
    this.cancelNotification('daily-note-reminder');
  }

  /**
   * Get all scheduled notifications
   */
  getScheduledNotifications(): ScheduledNotification[] {
    return Array.from(this.scheduledNotifications.values()).map(n => ({
      ...n,
      timerId: undefined, // Don't expose timerId
    }));
  }

  /**
   * Save scheduled notifications to localStorage
   */
  private saveScheduledNotifications(): void {
    const data = Array.from(this.scheduledNotifications.values()).map(n => ({
      id: n.id,
      title: n.title,
      body: n.body,
      scheduledTime: n.scheduledTime.toISOString(),
      data: n.data,
    }));
    localStorage.setItem('pkm-scheduled-notifications', JSON.stringify(data));
  }

  /**
   * Load scheduled notifications from localStorage
   */
  private loadScheduledNotifications(): void {
    try {
      const data = localStorage.getItem('pkm-scheduled-notifications');
      if (!data) return;

      const notifications = JSON.parse(data);
      const now = new Date();

      for (const n of notifications) {
        const scheduledTime = new Date(n.scheduledTime);
        if (scheduledTime > now) {
          this.scheduleNotification(n.id, n.title, n.body, scheduledTime, n.data);
        }
      }
    } catch (error) {
      console.error('Failed to load scheduled notifications:', error);
    }
  }

  /**
   * Format minutes to human-readable string
   */
  private formatMinutes(minutes: number): string {
    if (minutes < 60) {
      return `${minutes} minute${minutes === 1 ? '' : 's'}`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours} hour${hours === 1 ? '' : 's'}`;
    }
    return `${hours}h ${remainingMinutes}m`;
  }
}

// Singleton instance
export const notificationService = new NotificationService();
